import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import { Alert, Button, Container, Divider, Stack } from '@mui/material';

import PageHeader from '~/components/Common/Elements/PageHeader';

import { useGetCruiseLine } from '~/hooks/apis/cruises/useGetCruiseLines';

import { CruiseLine } from '~/services/cruises/CruiseLineService';
import RateGroupRuleService, { RateGroupRule } from '~/services/cruises/RateGroupRuleService';
import RateGroupService, { RateGroup } from '~/services/cruises/RateGroupService';

import InputCruiseLineSelect from '../../components/Selects/InputCruiseLineSelect';

import RateGroupRulesSection from './RateGroupRulesSection/RateGroupRulesSection';
import RateGroupsSection from './RateGroupsSection';

function RateGroupingPage() {
  const location = useLocation();
  const [cruiseLine, setCruiseLine] = useState<CruiseLine | undefined>();
  const [rateGroups, setRateGroups] = useState<Array<RateGroup>>([]);
  const [rateGroupRules, setRateGroupRules] = useState<Array<RateGroupRule>>([]);

  const [isFetchingRateGroup, setIsFetchingRateGroup] = useState(false);
  const [isFetchingPackageRules, setIsFetchingPackageRules] = useState(false);
  const vendorId = useMemo(() => new URLSearchParams(location.search).get('vendorId') ?? undefined, [location.search]);
  const initialCruiseLineQuery = useGetCruiseLine(vendorId);

  const handleCruiseLineChange = useCallback((cruiseLine: CruiseLine) => setCruiseLine(cruiseLine), []);

  const fetchData = useCallback(async (vendorId: string) => {
    await fetchRateGroup(vendorId);
    await fetchPackageRules(vendorId);
  }, []);

  const fetchRateGroup = useCallback(async (vendorId: string) => {
    if (isFetchingRateGroup) return;

    setRateGroups([]);
    setIsFetchingRateGroup(true);

    const response = await RateGroupService.list({ vendorId });
    setRateGroups(response.result);
    setIsFetchingRateGroup(false);
  }, []);

  const fetchPackageRules = useCallback(async (vendorId: string) => {
    if (isFetchingPackageRules) return;

    setRateGroupRules([]);
    setIsFetchingPackageRules(true);

    const response = await RateGroupRuleService.list({ vendorId });
    setRateGroupRules(response.result);
    setIsFetchingPackageRules(false);
  }, []);

  useEffect(() => {
    if (!cruiseLine) return;
    fetchData(cruiseLine.id);
  }, [cruiseLine, fetchData]);

  useEffect(() => {
    if (!vendorId) {
      setCruiseLine(undefined);
      return;
    }

    if (initialCruiseLineQuery.data) {
      setCruiseLine(initialCruiseLineQuery.data);
    }
  }, [initialCruiseLineQuery.data, vendorId]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Rate Grouping</title>
      </Helmet>

      <PageHeader title="Rate Grouping">
        <Button component={Link} to="/cruises/rate-grouping/rate-assignment-assistant" variant="contained">
          Review rate assignments
        </Button>
      </PageHeader>

      <Stack direction="column" spacing={3}>
        {initialCruiseLineQuery.isError && <Alert severity="error">Unable to load the selected cruise line.</Alert>}
        <InputCruiseLineSelect
          label="Cruise line"
          value={cruiseLine?.id ?? ''}
          onChange={handleCruiseLineChange}
          withApprovedOffers
          selectedCruiseLine={cruiseLine}
          disabled={isFetchingRateGroup || isFetchingPackageRules}
        />

        {cruiseLine && (
          <>
            <Divider />

            <RateGroupsSection
              vendorId={cruiseLine.id}
              isFetchingList={isFetchingRateGroup}
              rateGroups={rateGroups}
              setRateGroups={setRateGroups}
            />

            <Divider />

            <RateGroupRulesSection
              cruiseLine={cruiseLine}
              isFetchingList={isFetchingPackageRules}
              rateGroupRules={rateGroupRules}
              setRateGroupRules={setRateGroupRules}
              rateGroups={rateGroups}
            />
          </>
        )}
      </Stack>
    </Container>
  );
}

export default RateGroupingPage;
